(function() {
	'use strict';

	angular
		.module('assetCommon')
		.factory('jobService', jobService);

	function jobService($http, $q, $timeout, $log, _, uriUtility, httpUtility, assetCommonConfig, cancellationService, EventEmitter) {
		var jobCompletionPollIntervalSteps = [500, 1000, 5000];
		var requestsPerStep = 10;
		var waitingCount = 0;
		var jobChangeCancellationSource;
		var jobChangeEventName = 'job.change';
		var events = new EventEmitter({
			wildcard: false
		});

		function beginWaitingForJob() {
			waitingCount++;
			return {
				endWaitingForJob: function () {
					waitingCount--;
				}
			};
		}

		function isJobComplete(job) {
			return !!job.finished;
		}

		function awaitBulkJobCompletionAsync(jobIds, options) {
			// return promise that
			// -- resolves when all jobs complete successfully
			// -- rejects when all jobs complete, not all successfully
			// -- progress when any job changes status

			options = options || {};

			var pollingIntervalStep = 0;
			var intervalRequestCount = 0;
			var pollingInterval;

			function updateInterval() {
				if (intervalRequestCount >= requestsPerStep && pollingIntervalStep < jobCompletionPollIntervalSteps.length - 1) {
					pollingIntervalStep++;
					intervalRequestCount = 0;
				}

				pollingInterval = jobCompletionPollIntervalSteps[pollingIntervalStep];
				intervalRequestCount++;
			}

			var jobStatuses = jobIds.reduce(function (acc, jobId) {
				acc[jobId] = { id: jobId, status: null };
				return acc;
			}, {});

			var deferred = $q.defer();
			var waiting = beginWaitingForJob();

			(function awaitJobCompletionAsyncCore(jobIds, options) {
				return multiGetJobsAsync(jobIds, { cancellationToken: options.cancellationToken, shouldIncludeFullResponse: options.shouldIncludeFullResponse || options.responseFormatter })
					.then(function (response) {
						var remainingJobIds = [];

						var changedJobs = [];

						response.items.forEach(function (job) {
							var isJobPending = !isJobComplete(job);
							if (jobStatuses[job.id].status !== job.status && !isJobPending)
								changedJobs.push(job.id);

							var jobStatus = (options.shouldIncludeFullResponse || options.responseFormatter) ? job : _.pick(job, ['id', 'status']);
							if (options.responseFormatter && jobStatus.response && jobStatus.response.content)
								jobStatus.response.content = options.responseFormatter(jobStatus.response.content);

							jobStatuses[job.id] = jobStatus;
							if (isJobPending)
								remainingJobIds.push(job.id);
						});

						if (changedJobs.length)
							deferred.notify({ updatedJobs: changedJobs.map(function (id) { return jobStatuses[id]; }) });

						if (remainingJobIds.length) {
							updateInterval();

							return $timeout(function () {
								return awaitJobCompletionAsyncCore(remainingJobIds, options);
							}, pollingInterval, false);
						}
						else {
							return {
								jobs: _.values(jobStatuses)
							};
						}
					});
			})(jobIds, options)
				.then(function (result) {
					if (!_.any(result.jobs, { status: 'failed' }))
						deferred.resolve(result);
					else
						deferred.reject(result);
				})
				.catch(function (response) {
					deferred.reject(httpUtility.getErrorReason(response));
				})
				.finally(waiting.endWaitingForJob);

			return deferred.promise;
		}

		function multiGetJobsAsync(jobIds, options) {
			options = options || {};

			var request = {
				items: _.map(jobIds, function (id) { return { id: id }; })
			};

			return $http.post(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'jobs/get', { fields: '!items.request' }), request, { timeout: options.cancellationToken })
				.then(function (response) {
					return response.data;
				});
		}

		function cancelJobAsync(jobId, runnerId) {
			return $http.post(assetCommonConfig.assetServiceBaseUri + uriUtility.fromPattern('jobs/{id}/cancel', { id: jobId, runner: runnerId }), null);
		}

		function restartJobAsync(jobId, runnerId) {
			return $http.post(assetCommonConfig.assetServiceBaseUri + uriUtility.fromPattern('jobs/{id}/restart', { id: jobId, runner: runnerId }), null);
		}

		function adminGetJobServerStatusAsync(cancellationToken) {
			return $q.all({
				service: $http.get(assetCommonConfig.assetServiceBaseUri + 'jobs/service', { timeout: cancellationToken }),
				servers: $http.get(assetCommonConfig.assetServiceBaseUri + 'jobs/servers?status=paused,running&limit=100', { timeout: cancellationToken })
			})
			.then(function (responses) {
				return {
					service: responses.service.data,
					servers: responses.servers.data.items
				};
			});
		}

		function adminGetJobsAsync(status, cancellationToken) {
			var params = {
				status: _.isArray(status) ? status.join(',') : status,
				limit: 100,
				fields: 'items.(id,bucketId,status,agent,created,started,serverId,runnerId,request.content.(assetIds,ops.op))'
			};

			return $http.get(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'jobs/', params), { timeout: cancellationToken })
				.then(function (response) {
					return response.data;
				});
		}

		function adminGetJobUri(jobId) {
			return uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'jobs/{id}', { id: jobId });
		}

		function adminStartJobServerAsync(serverId) {
			return $http.post(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'jobs/servers/{id}/start', { id: serverId }), null);
		}

		function adminPauseJobServerAsync(serverId) {
			return $http.post(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'jobs/servers/{id}/pause', { id: serverId }), null);
		}

		function adminStopJobServerAsync(serverId) {
			return $http.post(uriUtility.fromPattern(assetCommonConfig.assetServiceBaseUri + 'jobs/servers/{id}/stop', { id: serverId }), null);
		}

		function adminStartJobServiceAsync() {
			return $http.post(assetCommonConfig.assetServiceBaseUri + 'jobs/service/start', null);
		}

		function adminPauseJobServiceAsync() {
			return $http.post(assetCommonConfig.assetServiceBaseUri + 'jobs/service/pause', null);
		}

		function addStatusChangedHandler(options, handler) {
			events.addListener(jobChangeEventName, handler);

			// create connection only when there is a client listening
			if (!jobChangeCancellationSource || jobChangeCancellationSource.token.isCancelled) {
				jobChangeCancellationSource = cancellationService.createCancellationSource();
				createJobChangeWsConnection(options, jobChangeCancellationSource.token);
			}
		}

		function removeStatusChangedHandler(handler) {
			events.removeListener(jobChangeEventName, handler);

			// close connection if no client is listening
			if (jobChangeCancellationSource && events.listenersAny(jobChangeEventName).length === 0) {
				jobChangeCancellationSource.cancel();
				jobChangeCancellationSource = null;
			}
		}

		function createJobChangeWsConnection(options, cancellationToken) {
			var params = {
				status: options.status || 'Pending,Running',
				limit: options.limit || 25,
				fields: options.fields || 'items.(id,bucketId,status,agent,created,started,finished,request.content.(ops),response.content.(id,metadata,file,kind))'
			};

			return $http.post(uriUtility.fromPattern('/proxy/websockets/files/v1/jobs/me', params), null)
				.then(function(response) {
					var jobChangeWSConnection = new WebSocket(response.data.url);
					jobChangeWSConnection.onopen = function() {
						$log.info('Job WebSocket connection opened!');
					};

					jobChangeWSConnection.onmessage = function(event) {
						var data = JSON.parse(event.data);
						$log.info('Job WebSocket message recieved!');
						events.emit(jobChangeEventName, data.items ? data.items : [data]);
					};

					jobChangeWSConnection.onclose = function(event) {
						$log.info('Job WebSocket connection closed!');
						$log.log(event);

						reconnect(options, cancellationToken);
					};

					cancellationToken.whenCanceled(function() {
						jobChangeWSConnection.close();
					});
				});
		}

		function reconnect(options, cancellationToken) {
			// if connection was not cancelled, attempt to reconnect after 3 seconds.
			if (cancellationToken.isCancelled)
				return;

			$timeout(function() {
				$log.info('Job WebSocket connection reconnecting in 3 seconds!');
				createJobChangeWsConnection(options, cancellationToken);
			}, 3000, false);
		}

		return {
			awaitBulkJobCompletionAsync: awaitBulkJobCompletionAsync,
			multiGetJobsAsync: multiGetJobsAsync,
			get isAwaiting() {
				return !!waitingCount;
			},
			cancelJobAsync: cancelJobAsync,
			restartJobAsync: restartJobAsync,
			adminGetJobServerStatusAsync: adminGetJobServerStatusAsync,
			adminGetJobsAsync: adminGetJobsAsync,
			adminGetJobUri: adminGetJobUri,
			adminStartJobServerAsync: adminStartJobServerAsync,
			adminPauseJobServerAsync: adminPauseJobServerAsync,
			adminStopJobServerAsync: adminStopJobServerAsync,
			adminStartJobServiceAsync: adminStartJobServiceAsync,
			adminPauseJobServiceAsync: adminPauseJobServiceAsync,
			addStatusChangedHandler: addStatusChangedHandler,
			removeStatusChangedHandler: removeStatusChangedHandler
		};
	}
	jobService.$inject = ['$http', '$q', '$timeout', '$log', '_', 'uriUtility', 'httpUtility', 'assetCommonConfig', 'cancellationService', 'EventEmitter'];
})();